import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import toast from 'react-hot-toast';
import './styles.scss';

const tempId = () => `temp_${Math.random().toString(36).substr(2, 9)}`;

function Index({ alarmDeviceTypeId, initialSubtypes = [], hasSpanishReports, hasFrenchReports }) {
  const [subtypes, setSubtypes] = useState([]);
  const isEditMode = !!alarmDeviceTypeId;
  const newRowInputRef = useRef(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [draggingKey, setDraggingKey] = useState(null);

  const sortSubtypes = (subtypesToSort) => {
    return [...subtypesToSort].sort((a, b) => {
      const posA = a.position === null || a.position === undefined ? Infinity : a.position;
      const posB = b.position === null || b.position === undefined ? Infinity : b.position;
      return posA - posB;
    });
  };

  useEffect(() => {
    const sorted = sortSubtypes(initialSubtypes);
    setSubtypes(sorted.map((st) => ({ ...st, key: st.id || tempId() })));
  }, [initialSubtypes]);

  useEffect(() => {
    if (newRowInputRef.current) {
      newRowInputRef.current.focus();
    }
  }, [subtypes.length]);

  const handleNameChange = (key, newName) => {
    setSubtypes(subtypes.map((st) => (st.key === key ? { ...st, type_name: newName } : st)));
  };

  const handleLocalizedNameChange = (key, field, newName) => {
    setSubtypes(subtypes.map((st) => (st.key === key ? { ...st, [field]: newName } : st)));
  };

  const handleSave = (subtype) => {
    const name = subtype.type_name.trim();
    if (!name) {
      if (subtype.isNew) {
        setSubtypes(subtypes.filter((st) => st.key !== subtype.key));
      }
      return;
    }
    if (isEditMode) {
      const isCreating = subtype.isNew;
      const url = isCreating
        ? `/alarm_device_types/${alarmDeviceTypeId}/alarm_device_subtypes`
        : `/alarm_device_subtypes/${subtype.id}`;
      const method = isCreating ? 'post' : 'put';
      const payload = {
        alarm_device_subtype: {
          type_name: name,
          type_name_es: subtype.type_name_es,
          type_name_fr: subtype.type_name_fr
        }
      };

      if (isCreating) {
        payload.alarm_device_subtype.position = subtype.position;
      }

      api[method](url, payload)
        .then((response) => {
          toast.success(isCreating ? 'Subtype Added!' : 'Subtype Updated!');
          const sorted = sortSubtypes(response.data);
          setSubtypes(sorted.map((st) => ({ ...st, key: st.id })));
        })
        .catch((error) => {
          const messages = error.response?.data?.errors || [`Failed to save subtype.`];
          toast.error(messages.join(', '));
        });
    }
  };

  const handleRemove = (subtype) => {
    if (isEditMode) {
      api
        .put(`/alarm_device_subtypes/${subtype.id}`, { alarm_device_subtype: { active: false } })
        .then((response) => {
          toast.success('Subtype removed.');
          const sorted = sortSubtypes(response.data);
          setSubtypes(sorted.map((st) => ({ ...st, key: st.id })));
        })
        .catch(() => toast.error('Failed to remove subtype.'));
    } else if (subtype.id?.toString().startsWith('temp_')) {
      setSubtypes(subtypes.filter((st) => st.key !== subtype.key));
    } else {
      setSubtypes(subtypes.map((st) => (st.key === subtype.key ? { ...st, _destroy: true } : st)));
    }
  };

  const handleAddRow = () => {
    const newPosition = subtypes.length;
    setSubtypes([
      ...subtypes,
      {
        key: tempId(),
        id: null,
        type_name: '',
        type_name_es: '',
        type_name_fr: '',
        isNew: true,
        position: newPosition
      }
    ]);
  };

  const handleDragSort = () => {
    const _subtypes = [...subtypes];
    const draggedItemContent = _subtypes.splice(dragItem.current, 1)[0];
    _subtypes.splice(dragOverItem.current, 0, draggedItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    setSubtypes(_subtypes);
    setDraggingKey(null);
    saveOrder(_subtypes);
  };

  const saveOrder = (currentSubtypes) => {
    const payload = {
      subtypes: currentSubtypes.filter((st) => !st.isNew).map((st, index) => ({ id: st.id, position: index }))
    };
    if (payload.subtypes.length > 0) {
      api
        .post('/alarm_device_subtypes/update_positions', payload)
        .then(() => toast.success('Order Saved!'))
        .catch(() => toast.error('Failed to save order.'));
    }
  };

  const visibleSubtypes = subtypes.filter((st) => !st._destroy);

  useEffect(() => {
    const requireSubtypeDiv = document.getElementById('require-subtype-group');
    if (requireSubtypeDiv) {
      if (visibleSubtypes.length > 0) {
        requireSubtypeDiv.classList.remove('hidden');
      } else {
        requireSubtypeDiv.classList.add('hidden');
      }
    }
  }, [visibleSubtypes.length]);

  return (
    <div className="control-group">
      <label className="control-label">Device Subtypes</label>
      <div className="controls">
        {!isEditMode &&
          subtypes.map((subtype, index) => (
            <React.Fragment key={subtype.key}>
              <input
                type="hidden"
                name={`alarm_device_type[alarm_device_subtypes_attributes][${index}][type_name]`}
                value={subtype.type_name}
              />
              {hasSpanishReports && (
                <input
                  type="hidden"
                  name={`alarm_device_type[alarm_device_subtypes_attributes][${index}][type_name_es]`}
                  value={subtype.type_name_es || ''}
                />
              )}
              {hasFrenchReports && (
                <input
                  type="hidden"
                  name={`alarm_device_type[alarm_device_subtypes_attributes][${index}][type_name_fr]`}
                  value={subtype.type_name_fr || ''}
                />
              )}
              <input
                type="hidden"
                name={`alarm_device_type[alarm_device_subtypes_attributes][${index}][position]`}
                value={index}
              />
              {subtype.id && !subtype.id.toString().startsWith('temp_') && (
                <input
                  type="hidden"
                  name={`alarm_device_type[alarm_device_subtypes_attributes][${index}][id]`}
                  value={subtype.id}
                />
              )}
              {subtype._destroy && (
                <input
                  type="hidden"
                  name={`alarm_device_type[alarm_device_subtypes_attributes][${index}][_destroy]`}
                  value="1"
                />
              )}
            </React.Fragment>
          ))}

        <table className="table table-striped" style={{ width: '70%' }}>
          <thead>
            <tr>
              <th style={{ width: '20px' }} />
              <th>Name</th>
              {hasSpanishReports && <th>Name (Spanish)</th>}
              {hasFrenchReports && <th>Name (French)</th>}
              <th style={{ width: '50px' }} />
            </tr>
          </thead>
          <tbody>
            {visibleSubtypes.map((subtype, index) => (
              <tr
                key={subtype.key}
                draggable
                onDragStart={() => {
                  dragItem.current = index;
                  setDraggingKey(subtype.key);
                }}
                onDragEnter={() => (dragOverItem.current = index)}
                onDragEnd={handleDragSort}
                onDragOver={(e) => e.preventDefault()}
                className={`subtype-table-row ${draggingKey === subtype.key ? 'is-dragging' : ''}`}
                style={{ cursor: 'grab' }}>
                <td>
                  <i className="fa fa-bars" />
                </td>
                <td>
                  <input
                    ref={subtype.isNew ? newRowInputRef : null}
                    type="text"
                    className="span12"
                    value={subtype.type_name}
                    placeholder="Enter subtype name..."
                    onChange={(e) => handleNameChange(subtype.key, e.target.value)}
                    onBlur={() => handleSave(subtype)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.target.blur();
                    }}
                  />
                </td>
                {hasSpanishReports && (
                  <td>
                    <input
                      type="text"
                      aria-label="Spanish subtype name"
                      className="span12"
                      value={subtype.type_name_es || ''}
                      placeholder="Enter Spanish name..."
                      onChange={(e) => handleLocalizedNameChange(subtype.key, 'type_name_es', e.target.value)}
                      onBlur={() => handleSave(subtype)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                      }}
                    />
                  </td>
                )}
                {hasFrenchReports && (
                  <td>
                    <input
                      type="text"
                      aria-label="French subtype name"
                      className="span12"
                      value={subtype.type_name_fr || ''}
                      placeholder="Enter French name..."
                      onChange={(e) => handleLocalizedNameChange(subtype.key, 'type_name_fr', e.target.value)}
                      onBlur={() => handleSave(subtype)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.target.blur();
                      }}
                    />
                  </td>
                )}
                <td style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-mini btn-danger"
                    onClick={() => handleRemove(subtype)}
                    title="Remove Subtype"
                    aria-label="Remove Subtype"
                    onMouseDown={(e) => e.preventDefault()}>
                    <i className="fa fa-light fa-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3 + (hasSpanishReports ? 1 : 0) + (hasFrenchReports ? 1 : 0)}>
                <button type="button" className="btn btn-small" onClick={handleAddRow}>
                  <i className="fa fa-plus" /> Add Subtype
                </button>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

Index.propTypes = {
  alarmDeviceTypeId: PropTypes.number,
  initialSubtypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      type_name: PropTypes.string,
      type_name_es: PropTypes.string,
      type_name_fr: PropTypes.string,
      active: PropTypes.bool,
      _destroy: PropTypes.bool,
      position: PropTypes.number
    })
  ),
  hasSpanishReports: PropTypes.bool,
  hasFrenchReports: PropTypes.bool
};

Index.defaultProps = {
  alarmDeviceTypeId: null,
  initialSubtypes: [],
  hasSpanishReports: false,
  hasFrenchReports: false
};

export default Index;
